import React, { forwardRef, useCallback, useEffect, useMemo, useState } from 'react';
import {
    Button,
    HStack,
    ListInputItem,
    Select,
    type SelectProps,
    type SelectTriggerProps,
    Spinner,
    Text,
} from '@direct-frontend/components';
import { castRef, useDebouncedValue } from '@direct-frontend/stdlib';
import LocalityIcon from './assets/LocalityIcon.react.svg';
import { useLazyGeoSuggestQuery } from '../../api/api';
import styles from './LocalityInput.module.css';

const makeRenderTrigger = (value: string = '') =>
    forwardRef<HTMLElement, SelectTriggerProps<string>>(function ButtonTrigger(props, forwardedRef) {
        const { placeholder, ...other } = props;

        return (
            <Button
                {...other}
                iconLeft={LocalityIcon}
                size="m"
                width="max"
                color="text"
                ref={castRef(forwardedRef, 'button')}
            >
                {value.split(/,\s*/).reverse().join(', ')}
            </Button>
        );
    });

type LocalityInputProps = Omit<SelectProps<string>, 'value' | 'pinnedSection' | 'items' | 'color'> & {
    value?: string;
    onChange?: (value: string) => void;
    onInputChange?: (value: string) => void;
};

export const LocalityInput = (props: LocalityInputProps) => {
    const { value, onChange, onInputChange, ...other } = props;
    const [searchText, setSearchText] = useState('');
    const debounceSearchText = useDebouncedValue(searchText, 300);
    const [fetchGeoObjects, { data: items = [], isLoading }] = useLazyGeoSuggestQuery();

    useEffect(() => {
        if (!debounceSearchText || value === debounceSearchText) {
            return;
        }

        fetchGeoObjects({ text: debounceSearchText, results: 5, types: 'locality' });
    }, [value, debounceSearchText, fetchGeoObjects]);

    const loadingSuggest = useMemo(
        () => [
            {
                value: 'Загрузка...',
                disabled: true,
                content: (
                    <HStack justifyContent="center" alignItems="center" className={styles.item}>
                        <Spinner />
                    </HStack>
                ),
            },
        ],
        []
    );

    const emptySuggest = useMemo(
        () => [
            {
                value: 'Ничего не найдено',
                disabled: true,
                content: (
                    <HStack justifyContent="center" alignItems="center">
                        <Text role="supplementary">{searchText ? 'Ничего не найдено' : 'Начните вводить запрос'}</Text>
                    </HStack>
                ),
            },
        ],
        [searchText]
    );

    const currentSuggestList = useMemo(() => {
        if (isLoading) {
            return loadingSuggest;
        }

        if (items.length === 0 && !value) {
            return emptySuggest;
        }

        return items.filter(Boolean).map((item: string) => {
            return {
                value: item,
                content: <Text className={styles.item}>{item}</Text>,
            };
        });
    }, [isLoading, loadingSuggest, emptySuggest, value, items]);

    const handleSelectionChange = useCallback(
        (value: unknown) => {
            const normalizedValue = value?.toString();

            if (normalizedValue) {
                onChange?.(normalizedValue);
                setSearchText(normalizedValue);
            }
        },
        [onChange]
    );

    return (
        <Select
            {...other}
            flip="once"
            color="contour"
            value={value}
            maxHeight={320}
            displaySelection={false}
            items={currentSuggestList}
            renderTrigger={makeRenderTrigger(value)}
            pinnedSection={<ListInputItem placeholder="Поиск" value={searchText} onChange={setSearchText} />}
            onChange={handleSelectionChange}
        />
    );
};
